import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaTrash } from "react-icons/fa";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import { useCart } from "../../context/CartContext";

const Cart = () => {
  const { t } = useTranslation(["common", "products"]);
  const { items, subtotal, itemCount, removeItem, updateQuantity } = useCart();

  if (itemCount === 0) {
    return (
      <Layout title={t("common:cart") || "購物車"}>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-space-light border border-white/10 p-8 rounded-full mb-6">
            <svg
              className="w-16 h-16 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h2 className="text-h2 mb-2 text-white">
            {t("common:cart_empty") || "您的購物車是空的"}
          </h2>
          <p className="text-body text-gray-400 mb-8 max-w-md">
            {t("common:cart_empty_desc")}
          </p>
          <Link to="/products">
            <Button
              size="lg"
              className="bg-neon-green text-space hover:opacity-90"
            >
              {t("common:start_shopping") || "開始購物"}
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={t("common:cart") || "購物車"}>
      <div className="space-y-6">
        {items.map((item) => (
          <div
            key={item.productId}
            className="flex flex-col sm:flex-row gap-4 p-4 md:p-6 rounded-2xl bg-space-light/50 border border-white/10"
          >
            <Link
              to={`/products/${item.productId}`}
              className="shrink-0 w-full sm:w-32 h-24 rounded-lg overflow-hidden border border-white/10"
            >
              <img
                src={item.imageUrl}
                alt={item.name}
                className="w-full h-full object-cover"
              />
            </Link>

            <div className="flex-1 min-w-0">
              <Link
                to={`/products/${item.productId}`}
                className="text-h4 text-white hover:text-neon-green transition-colors line-clamp-2"
              >
                {item.name}
              </Link>
              <p className="text-neon-green font-bold mt-2 tabular-nums">
                HKD {item.price.toLocaleString()}
              </p>
            </div>

            <div className="flex items-center gap-4 sm:flex-col sm:items-end justify-between sm:justify-center">
              <div className="flex items-center border border-white/20 rounded-lg bg-space/50">
                <button
                  type="button"
                  onClick={() =>
                    updateQuantity(item.productId, item.quantity - 1)
                  }
                  disabled={item.quantity <= 1}
                  className="px-3 py-1.5 text-gray-300 hover:text-neon-green disabled:opacity-40"
                >
                  -
                </button>
                <span className="w-10 text-center text-body-sm text-white tabular-nums">
                  {item.quantity}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    updateQuantity(item.productId, item.quantity + 1)
                  }
                  className="px-3 py-1.5 text-gray-300 hover:text-neon-green"
                >
                  +
                </button>
              </div>

              <button
                type="button"
                onClick={() => removeItem(item.productId)}
                className="flex items-center gap-1 text-body-sm text-gray-400 hover:text-red-400 transition-colors"
              >
                <FaTrash size={14} />
                {t("products:cart_remove") || "移除"}
              </button>
            </div>
          </div>
        ))}

        <div className="rounded-2xl bg-space-light/50 border border-white/10 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-body-sm text-gray-400">
              {t("products:cart_subtotal") || "小計"} ({itemCount}{" "}
              {t("products:items") || "項"})
            </p>
            <p className="text-h2 text-neon-green font-bold tabular-nums">
              HKD {subtotal.toLocaleString()}
            </p>
          </div>
          <Link to="/checkout">
            <Button
              size="lg"
              className="bg-gradient-brand text-space hover:opacity-90 shrink-0"
            >
              {t("products:cart_checkout") || "前往結帳"}
            </Button>
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default Cart;
